import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Horizontal scrollbar pinned to the bottom of the screen while a wide
// scroll area is on screen but its own scrollbar is below the fold.
export const PinnedScrollbar = ({ targetRef, testid = "pinned-scrollbar" }) => {
    const barRef = useRef(null);
    const [box, setBox] = useState(null);

    useEffect(() => {
        const el = targetRef.current;
        if (!el) return undefined;
        const layout = () => {
            const r = el.getBoundingClientRect();
            const vh = window.innerHeight;
            const show = el.scrollWidth > el.clientWidth + 1 && r.top < vh - 48 && r.bottom > vh;
            const next = show ? { left: r.left, width: r.width, scrollWidth: el.scrollWidth } : null;
            setBox((prev) => (
                prev && next && prev.left === next.left && prev.width === next.width && prev.scrollWidth === next.scrollWidth ? prev : next
            ));
        };
        const syncFromTarget = () => {
            if (barRef.current && barRef.current.scrollLeft !== el.scrollLeft) barRef.current.scrollLeft = el.scrollLeft;
        };
        layout();
        window.addEventListener("scroll", layout, true);
        window.addEventListener("resize", layout);
        el.addEventListener("scroll", syncFromTarget);
        const ro = new ResizeObserver(layout);
        ro.observe(el);
        if (el.firstElementChild) ro.observe(el.firstElementChild);
        return () => {
            window.removeEventListener("scroll", layout, true);
            window.removeEventListener("resize", layout);
            el.removeEventListener("scroll", syncFromTarget);
            ro.disconnect();
        };
    }, [targetRef]);

    useEffect(() => {
        if (box && barRef.current && targetRef.current) barRef.current.scrollLeft = targetRef.current.scrollLeft;
    }, [box, targetRef]);

    if (!box) return null;
    return createPortal(
        <div
            ref={barRef}
            data-testid={testid}
            onScroll={(e) => {
                const el = targetRef.current;
                if (el && el.scrollLeft !== e.currentTarget.scrollLeft) el.scrollLeft = e.currentTarget.scrollLeft;
            }}
            className="fixed bottom-0 z-40 h-3.5 overflow-x-scroll overflow-y-hidden border-t border-slate-200 bg-white/90 backdrop-blur-md"
            style={{ left: box.left, width: box.width }}
        >
            <div style={{ width: box.scrollWidth, height: 1 }} />
        </div>,
        document.body,
    );
};
